import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from 'components/loadingbutton/LoadingButton';
import { apiDocumentServiceRepository } from 'services/repositories/implementations/ApiDocumentServiceRepository';
import {
  EMBED_PROVIDER_CONFIG,
  ERROR_TRANSLATION_KEY,
  parseEmbedCode,
  type EmbedContent,
  type EmbedSubtype,
  type EmbedValidationStatus,
  type ParsedEmbedCode,
} from 'components/editor/embed/embed';
import { embedDialogTestIds } from './EmbedDialog.test-ids';

type EmbedDialogProps = {
  open: boolean;
  subtype: EmbedSubtype;
  embedContent: Partial<EmbedContent>;
  onClose: () => void;
  onSubmit: (embedUrl: string, subtype: EmbedSubtype, dimensions: ParsedEmbedCode['dimensions']) => void;
};

export default function EmbedDialog({ open, subtype, embedContent, onClose, onSubmit }: EmbedDialogProps) {
  const { t } = useTranslation();
  const initialValue = embedContent.embedUrl ?? '';

  const [value, setValue] = useState(initialValue);
  const [touched, setTouched] = useState(false);
  const [formatError, setFormatError] = useState<string | undefined>(undefined);
  const [validationStatus, setValidationStatus] = useState<EmbedValidationStatus | undefined>(embedContent.validationStatus);
  const [isValidating, setIsValidating] = useState(false);

  const providerConfig = EMBED_PROVIDER_CONFIG[subtype];

  const backendErrorKey = !touched && validationStatus ? ERROR_TRANSLATION_KEY[validationStatus] : undefined;
  const errorMessage = formatError ?? (backendErrorKey ? t(backendErrorKey, { provider: providerConfig.providerName }) : undefined);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    setTouched(true);
    setFormatError(undefined);
  };

  const handleSubmit = async () => {
    const trimmedValue = value.trim();
    const { embedUrl, dimensions } = parseEmbedCode(trimmedValue, subtype);
    setIsValidating(true);
    try {
      const response = await apiDocumentServiceRepository.validateEmbed({ embedUrl });
      if (response.validationStatus === 'REACHABLE') {
        onSubmit(embedUrl, subtype, dimensions);
        return;
      }
      setTouched(false);
      setValidationStatus(response.validationStatus);
    } catch {
      setTouched(false);
      setValidationStatus(undefined);
      setFormatError(t('document.grid.embed.dialog_error_validation_failed'));
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="embed-dialog-title"
      data-testid={embedDialogTestIds.root}
      PaperProps={{ onMouseDown: (event) => event.stopPropagation() }}
    >
      <DialogTitle id="embed-dialog-title" sx={{ m: 0, px: 3, pt: 3, pb: 2 }}>
        <Typography variant="h6" component="span" fontWeight={600}>
          {t(providerConfig.titleKey)}
        </Typography>
        <IconButton
          aria-label={t('close_dialog')}
          onClick={onClose}
          sx={{ position: 'absolute', right: 16, top: 16, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 0 }}>
        <Typography variant="body1">{t('document.grid.embed.dialog_step_1_label')}</Typography>
        <Typography variant="body2" sx={{ mt: 1.5 }} data-testid={embedDialogTestIds.step1Instructions}>
          {t(providerConfig.step1InstructionsKey)}{' '}
          <Link href={providerConfig.docsUrl} target="_blank" rel="noopener noreferrer" underline="always" color="inherit">
            {t('document.grid.embed.dialog_learn_more')}
          </Link>
          {'. '}
          <br />
          <br />
          {t('document.grid.embed.dialog_step_1_followup')}
        </Typography>
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="body1">{t('document.grid.embed.dialog_step_2_label')}</Typography>
        <Typography variant="body2" sx={{ mt: 1.5 }} data-testid={embedDialogTestIds.step2Instructions}>
          {t(providerConfig.descriptionKey)}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={4}
          sx={{ mt: 2.5 }}
          inputProps={{ 'data-testid': embedDialogTestIds.urlInput }}
          label={t(providerConfig.inputLabelKey)}
          placeholder={t('document.grid.embed.dialog_input_placeholder')}
          value={value}
          onChange={handleChange}
          error={Boolean(errorMessage)}
          helperText={errorMessage}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
        <LoadingButton data-testid={embedDialogTestIds.submitButton} color="primary" onClick={handleSubmit} isLoading={isValidating}>
          {t('document.grid.embed.dialog_submit_button')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
